import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  onAuthStateChanged,
  updateProfile,
  browserLocalPersistence,
  getRedirectResult,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type User,
  type Auth
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  getDoc,
  query,
  where,
  serverTimestamp,
  enableNetwork,
  disableNetwork,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type DocumentData,
  type FirestoreError,
  type Firestore
} from 'firebase/firestore';
import { useStore } from '../store/useStore';
import { encrypt, decrypt } from './encryption';
import type { ChatSession, APIConfig, FirestoreMessage } from '../types/models';

// Environment variables
const ENV = {
  FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
  FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID
} as const;

// Validate environment variables
Object.entries(ENV).forEach(([key, value]) => {
  if (!value) {
    throw new Error(`Missing required Firebase configuration: ${key}`);
  }
});

// Firebase configuration
const firebaseConfig = {
  apiKey: ENV.FIREBASE_API_KEY,
  authDomain: 'pildoria-chat.firebaseapp.com',
  projectId: ENV.FIREBASE_PROJECT_ID,
  storageBucket: ENV.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: ENV.FIREBASE_MESSAGING_SENDER_ID,
  appId: ENV.FIREBASE_APP_ID
};

// Initialize Firebase services
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = initializeFirestore(app, {
  cacheSizeBytes: 50 * 1024 * 1024,
  experimentalForceLongPolling: true
});
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

// Function to test connection state
const checkConnection = async (): Promise<boolean> => {
  try {
    const ref = doc(db, 'public_test', 'connection_test');
    await getDoc(ref);
    console.log('Firestore connection test successful');
    return true;
  } catch (err: unknown) {
    console.error('Firestore connection test failed:', err);
    return false;
  }
};

// Set up network monitoring
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('Browser went online. Enabling Firestore network.');
    void enableNetwork(db).catch((err: FirestoreError) => 
      console.error('Error enabling network:', err)
    );
  });

  window.addEventListener('offline', () => {
    console.log('Browser went offline. Disabling Firestore network.');
    void disableNetwork(db).catch((err: FirestoreError) => 
      console.error('Error disabling network:', err)
    );
  });

  // Test initial connection
  void checkConnection().then(isConnected => {
    console.log('Initial connection state:', isConnected ? 'Connected' : 'Disconnected');
  });
}

// Log initialization
console.log('Firebase initialized with config:', {
  ...firebaseConfig,
  apiKey: '[REDACTED]'
});

// Export Firebase instances and utilities
export {
  app,
  auth,
  db,
  googleProvider,
  checkConnection as checkFirestoreConnection
};

// Export authentication functions
export const signUp = async (email: string, password: string, displayName: string) => {
  console.log('Starting signup process...');
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log('User created:', userCredential.user.uid);

    if (displayName) {
      await updateProfile(userCredential.user, { displayName });
      console.log('Display name updated:', displayName);
    }

    const store = useStore.getState();
    const currentLanguage = store.language;
    localStorage.setItem('language', currentLanguage);
    
    const userDocRef = doc(db, 'users', userCredential.user.uid);
    await setDoc(userDocRef, {
      language: currentLanguage,
      createdAt: serverTimestamp(),
      topUpValue: 1
    });

    await store.setLanguage(currentLanguage);
    return userCredential.user;
  } catch (error) {
    console.error('Signup error:', error);
    throw error;
  }
};

export const login = async (email: string, password: string) => {
  console.log('Starting login process...');
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('User logged in:', userCredential.user.uid);
    return userCredential.user;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const signInWithGoogle = async () => {
  console.log('Starting Google sign in...');
  try {
    const result = await signInWithPopup(auth, googleProvider);
    console.log('Google sign in successful:', result.user.uid);
    await handleGoogleSignInSuccess(result.user);
    return result.user;
  } catch (error) {
    console.error('Google sign in error:', error);
    throw error;
  }
};

// Helper function to handle successful Google sign in
const handleGoogleSignInSuccess = async (user: User) => {
  const store = useStore.getState();
  const currentLanguage = store.language;
  localStorage.setItem('language', currentLanguage);
  
  const userDocRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userDocRef);
  
  if (!userDoc.exists()) {
    await setDoc(userDocRef, {
      language: currentLanguage,
      createdAt: serverTimestamp(),
      topUpValue: 1
    });
  } else {
    await setDoc(userDocRef, { language: currentLanguage }, { merge: true });
  }

  await store.setLanguage(currentLanguage);
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Export Firestore functions
export const saveChatHistory = async (userId: string, chat: ChatSession) => {
  try {
    const chatRef = doc(db, 'users', userId, 'chatHistory', chat.id);
    await setDoc(chatRef, {
      ...chat,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error saving chat history:', error);
    throw error;
  }
};

export const getChatHistory = async (userId: string): Promise<ChatSession[]> => {
  try {
    const historyRef = collection(db, 'users', userId, 'chatHistory');
    const snapshot = await getDocs(historyRef);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || 'New Chat',
        messages: Array.isArray(data.messages) ? data.messages : [],
        createdAt: typeof data.createdAt === 'number' ? data.createdAt :
                  data.createdAt?.toMillis?.() ||
                  data.updatedAt?.toMillis?.() ||
                  Date.now(),
        modelId: data.modelId,
        context: data.context || { userInfo: {}, systemPrompt: '' }
      } as ChatSession;
    });
  } catch (error) {
    console.error('Error getting chat history:', error);
    throw error;
  }
};

export const saveApiConfig = async (userId: string, config: APIConfig) => {
  try {
    if (!config.apiKey) {
      throw new Error('API key is required');
    }
    const encryptedKey = await encrypt(config.apiKey);
    const configRef = doc(db, 'users', userId, 'apiConfigs', config.provider);
    await setDoc(configRef, {
      ...config,
      apiKey: encryptedKey
    });
    return true;
  } catch (error) {
    console.error('Error saving API config:', error);
    throw error;
  }
};

export const getApiConfigs = async (userId: string): Promise<APIConfig[]> => {
  try {
    const configsRef = collection(db, 'users', userId, 'apiConfigs');
    const snapshot = await getDocs(configsRef);
    
    const configs = await Promise.all(snapshot.docs.map(async doc => {
      const data = doc.data();
      if (!data.apiKey) {
        console.warn('No API key found for provider:', doc.id);
        return null;
      }
      try {
        const decryptedKey = await decrypt(data.apiKey);
        return {
          ...data,
          apiKey: decryptedKey,
          provider: doc.id
        } as APIConfig;
      } catch (error) {
        console.error('Error decrypting API key for provider:', doc.id, error);
        return null;
      }
    }));
    
    return configs.filter((config): config is APIConfig => config !== null);
  } catch (error) {
    console.error('Error getting API configs:', error);
    throw error;
  }
};

export const getChatDocument = async (userId: string, chatId: string) => {
  try {
    console.log('Getting chat document:', { userId, chatId });
    const chatRef = doc(db, 'users', userId, 'chatHistory', chatId);
    return await getDoc(chatRef);
  } catch (error) {
    console.error('Error getting chat document:', error);
    throw error;
  }
};

export const saveChatMessage = async (message: FirestoreMessage, chatId: string) => {
  try {
    console.log('Saving chat message:', { chatId, message });
    const chatRef = doc(db, 'users', message.userId, 'chatHistory', chatId);
    
    const chatDoc = await getDoc(chatRef);
    const currentData = chatDoc.data();
    const currentMessages = currentData?.messages || [];
    
    const updatedMessages = [...currentMessages, {
      ...message,
      id: message.id || `${message.role}-${Date.now()}`
    }];
    
    await setDoc(chatRef, {
      messages: updatedMessages,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    console.log('Chat message saved successfully');
    return true;
  } catch (error) {
    console.error('Error saving chat message:', error);
    throw error;
  }
};

export const updateUserLanguage = async (userId: string, language: string) => {
  try {
    console.log('Updating user language:', { userId, language });
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { language }, { merge: true });
    
    // Update store language
    const store = useStore.getState();
    await store.setLanguage(language);
    
    // Update localStorage
    localStorage.setItem('language', language);
    
    console.log('Language updated successfully');
    return true;
  } catch (error) {
    console.error('Error updating user language:', error);
    throw error;
  }
};

export const deleteApiKey = async (userId: string, provider: string) => {
  try {
    const configRef = doc(db, 'users', userId, 'apiConfigs', provider);
    await deleteDoc(configRef);
    return true;
  } catch (error) {
    console.error('Error deleting API key:', error);
    throw error;
  }
};

export const deleteUserAndData = async (userId: string) => {
  try {
    console.log('Starting user deletion process for:', userId);
    
    // Delete user's data from Firestore
    const userRef = doc(db, 'users', userId);
    const chatHistoryRef = collection(db, 'users', userId, 'chatHistory');
    const apiConfigsRef = collection(db, 'users', userId, 'apiConfigs');

    // Delete chat history
    const chatDocs = await getDocs(chatHistoryRef);
    for (const doc of chatDocs.docs) {
      await deleteDoc(doc.ref);
    }

    // Delete API configs
    const configDocs = await getDocs(apiConfigsRef);
    for (const doc of configDocs.docs) {
      await deleteDoc(doc.ref);
    }

    // Delete user document
    await deleteDoc(userRef);

    // Delete user from Firebase Authentication
    const currentUser = auth.currentUser;
    if (currentUser) {
      await currentUser.delete();
      console.log('User successfully deleted from Authentication');
    }

    return true;
  } catch (error) {
    console.error('Error deleting user and data:', error);
    throw error;
  }
};